import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useGroupAttendanceSummary,
  useGroupLessons,
  useGroupPerformanceSummary,
} from 'core/hooks';
import { LoadingIndicator } from 'atoms';

import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import { ScheduleCard } from '../parts/ScheduleCard';
import { LessonsTable } from '../parts/LessonsTable';
import { AttendanceCard } from '../parts/AttendanceCard';
import { PerformanceCard } from '../parts/PerformanceCard';

interface IScheduleTabProps {
  groupId: string;
}

/**
 * ScheduleTab — the «when does this group meet, who's behind» view.
 * Pulls three lazy queries on first mount:
 *   - `GroupLessonsQuery` — schedule snapshot + lessons table data,
 *   - `groupAttendanceSummary` — TZ §G3,
 *   - `groupPerformanceSummary` — TZ §G4.
 *
 * Each card renders independently; partial failures degrade
 * gracefully (a missing summary just shows «no data»).
 */
export const ScheduleTab = memo(({ groupId }: IScheduleTabProps) => {
  const { t } = useTranslation();
  const { group, loading: lessonsLoading } = useGroupLessons(groupId);
  const {
    data: attendance,
    loading: attendanceLoading,
  } = useGroupAttendanceSummary(groupId);
  const {
    data: performance,
    loading: performanceLoading,
  } = useGroupPerformanceSummary(groupId);

  if (lessonsLoading && !group) {
    return (
      <S.CenterLoader>
        <LoadingIndicator />
      </S.CenterLoader>
    );
  }

  if (!group) {
    return <S.EmptyState>{t('pages.groupDetail.schedule.empty')}</S.EmptyState>;
  }

  return (
    <S.DimWhenFetching fetching={attendanceLoading || performanceLoading}>
      <ScheduleCard group={group} />
      <AttendanceCard summary={attendance} />
      <PerformanceCard summary={performance} />
      <LessonsTable
        lessons={(group.lessons || []).filter(Boolean) as never[]}
      />
    </S.DimWhenFetching>
  );
});
